import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  effect,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';
import { FormlyModule } from '@ngx-formly/core';
import { NgClass } from '@angular/common';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { map, of, switchMap, takeUntil, tap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModulesStore } from '../../../../modules.store';
import { cloneDeep } from 'lodash-es';
import { FormGroup } from '@angular/forms';
import { EditFormRightsDirective } from '../../../../../../../core/auth/directives/can-edit-form.directive';
import { EditModuleRightsDirective } from '../../../../../../../core/auth/directives/can-edit-module.directive';
import type { Entry } from '../../../../modules.model';
import { EntryState } from '../../../../modules.enum';
import { EntriesService } from '../../../../features/entries/entries.service';
import { UtilityService } from '../../../../../../../shared/services/utility.service';
import { FormService } from '../../../../features/form-builder/form.service';
import { FuseAlertComponent } from '../../../../../../../../@fuse/components/alert';
import { UserStore } from 'app/core/services/user.store';
import { canReadFieldRight, canReadGeneratedField, effectiveFieldRightForKeyPath } from '../../../../field-rights.util';

@Component({
  selector: 'app-enrollment-wrapper',
  standalone: true,
  template: `
    <div class="flex flex-col w-full gap-4">
      <!-- Assignment header section -->
      <div class="p-6 rounded-lg fuse-card border shadow-sm">
        <div class="flex flex-row justify-between items-center mb-4">
          <div>
            <h2 class="text-xl">
              {{ canReadAssignmentField('entry.title') ? model?.title || 'Nieuwe opdracht' : 'Opdracht' }}
            </h2>
            <div class="flex items-center gap-2 text-gray-400 mt-1 text-sm">
              @if (canReadAssignmentField('entry.company')) {
                <span class="flex items-center gap-1">
                  <mat-icon class="icon-size-4">business</mat-icon>&nbsp;
                  {{
                    readableReferenceLabel(model?.company, 'companies') ||
                      (model?.company ? 'Afgeschermd' : 'Geen opdrachtgever')
                  }}
                </span>
              }
              @if (canReadAssignmentField('entry.company') && canReadAssignmentField('entry.start-date')) {
                <span>•</span>
              }
              @if (canReadAssignmentField('entry.start-date')) {
                <span class="flex items-center gap-1">
                  <mat-icon class="icon-size-4">event</mat-icon>&nbsp;
                  {{ formatDate(model?.['start-date']) || 'Geen startdatum' }}
                </span>
              }
            </div>
          </div>
          @if (canReadAssignmentField('entry.status')) {
            <div>
              <div class="status-badge" [ngClass]="getStatusClass()">
                {{ model?.status?.selectedInput || 'Geen status' }}
              </div>
            </div>
          }
        </div>

        <!-- Assignment details section -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Location -->
          @if (canReadAssignmentField('entry.location')) {
            <div class="detail-item">
              <div class="detail-label">Locatie</div>
              <div class="detail-value flex items-center gap-2">
                <mat-icon>location_on</mat-icon>
                {{
                  readableReferenceLabel(model?.location, 'locations') ||
                    (model?.location ? 'Afgeschermd' : 'Geen locatie opgegeven')
                }}
              </div>
            </div>
          }

          <!-- Activity -->
          @if (canReadAssignmentField('entry.activity')) {
            <div class="detail-item">
              <div class="detail-label">Activiteit</div>
              <div class="detail-value flex items-center gap-2">
                <mat-icon>category</mat-icon>
                {{
                  readableReferenceLabel(model?.activity, 'activities') ||
                    (model?.activity ? 'Afgeschermd' : 'Geen activiteit opgegeven')
                }}
              </div>
            </div>
          }

          <!-- Rate -->
          @if (canReadAssignmentField('entry.rate')) {
            <div class="detail-item">
              <div class="detail-label">Tarief</div>
              <div class="detail-value flex items-center gap-2">
                <mat-icon svgIcon="coins"></mat-icon>
                {{ formatRate() || 'Geen tarief opgegeven' }}
              </div>
            </div>
          }

          <!-- Hours -->
          @if (canReadAssignmentField('entry.hours')) {
            <div class="detail-item">
              <div class="detail-label">Uren</div>
              <div class="detail-value flex items-center gap-2">
                <mat-icon>schedule</mat-icon>
                {{ formatHours() || 'Geen uren opgegeven' }}
              </div>
            </div>
          }
        </div>

        <!-- Description -->
        @if (canReadAssignmentField('entry.description') && model?.description) {
          <div class="mt-8">
            <h3 class="text-lg font-medium mb-4">Opdrachtomschrijving</h3>
            <div class="rounded whitespace-pre-line text-md">
              {{ this.model?.description }}
            </div>
          </div>
        }

        <!-- Requested profiles section -->
        @if (
          canReadAssignmentField('entry.request.role') ||
          canReadAssignmentField('entry.request.competencies') ||
          canReadAssignmentField('entry.request.amount')
        ) {
          <h3 class="text-lg font-medium mt-8 mb-4">Gevraagde profiel</h3>

          <div class="grid grid-cols-1 gap-4">
            <div class="p-3 border rounded-lg flex flex-col sm:flex-row justify-between gap-3">
              <div class="flex-1">
                @if (canReadAssignmentField('entry.request.role')) {
                  <div class="font-medium text-primary">
                    {{
                      readableReferenceLabel(model.request?.role, 'user-roles') ||
                        (model.request?.role ? 'Afgeschermd' : 'Geen functie opgegeven')
                    }}
                  </div>
                }
                @if (canReadAssignmentField('entry.request.competencies') && model?.request?.competencies?.length) {
                  <div class="text-sm text-gray-400 mt-1">
                    <span class="mr-1">Competenties:</span>
                    @for (comp of model?.request?.competencies; track comp; let last = $last) {
                      <span class="competency">
                        {{ readableReferenceLabel(comp, 'competences') || 'Afgeschermd' }}
                      </span>
                    }
                  </div>
                }
              </div>
              @if (canReadAssignmentField('entry.request.amount')) {
                <div class="flex-0 flex items-center">
                  <div class="p-2 px-4 rounded-full text-sm">
                    {{ model?.request?.amount || 1 }} {{ model?.request?.amount > 1 ? 'Personen' : 'Persoon' }}
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </div>

      <!-- Hidden formly fields required for the form -->
      <div class="hidden">
        @for (field of allFields(); track field) {
          <formly-field [field]="field"></formly-field>
        }
      </div>

      <!-- Enrollment section -->
      <div class="w-full inline-block mt-4">
        <h3 class="text-xl font-medium text-center">Inschrijven op deze opdracht</h3>
      </div>
      <div class="rounded-lg fuse-card border shadow-sm p-4 max-w-screen-sm mx-auto">
        <!-- Show message when assignment is closed -->
        @if (isAssignmentClosed()) {
          <fuse-alert class="mb-4" [type]="'info'" data-cy="assignment-closed"
            >Deze opdracht is afgerond. Inschrijven is niet meer mogelijk.</fuse-alert
          >
        }

        <div class="enrollment-form" [class.opacity-50]="isAssignmentClosed()">
          @if (enrollmentModuleFields().length) {
            <formly-form
              [form]="subForm"
              [canEditForm]="{ form: subForm, id: enrollmentModule()?.id }"
              [options]="subFormOptions"
              [fields]="enrollmentModuleFields()"></formly-form>
          }
          <div class="inline-block w-full">
            @if (isEditting() && !isAssignmentClosed()) {
              <fuse-alert class="mb-4 clear" [type]="'success'"
                >LET OP: Je hebt al op deze opdracht gereageerd.</fuse-alert
              >
            }
            @if (saved()) {
              <fuse-alert class="mb-4 clear" [type]="'success'" data-cy="assignment-saved"
                >Bedankt voor je inschrijving. We zullen je aanvraag zo snel mogelijk behandelen.</fuse-alert
              >
            }
            @if (isEditting() && !isAssignmentClosed()) {
              <button class="float-left mr-4" mat-stroked-button color="warn" type="button" (click)="delete()">
                Inschrijving intrekken
              </button>
            }
            @if (!saved() && !isAssignmentClosed()) {
              <div>
                <button
                  class="flex-0 ml-auto dd-action-button"
                  mat-flat-button
                  type="button"
                  (click)="save()"
                  *canEditModule="{
                    moduleId: enrollmentModule().id,
                    show: true,
                  }">
                  {{ isEditting() ? 'Opslaan' : 'Inschrijven' }}
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    .status-badge {
      padding: 6px 12px;
      border-radius: 16px;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
    }

    .status-default,
    .status-open {
      color: var(--fuse-primary);
    }

    .status-geannulleerd {
      color: var(--fuse-warmn);
    }

    .detail-item {
      position: relative;
    }

    .detail-label {
      font-size: 12px;
      display: none;
      margin-bottom: 4px;
    }

    .detail-value {
      font-size: 14px;
    }

    .competency {
      font-size: 13px;
    }
  `,
  imports: [
    FormlyModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatCardModule,
    MatTooltipModule,
    NgClass,
    EditFormRightsDirective,
    EditModuleRightsDirective,
    FuseAlertComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnrollmentWrapper extends FieldWrapper implements OnInit, AfterViewInit {
  // Group fields by section
  modulesStore = inject(ModulesStore);
  utilityService = inject(UtilityService);
  entriesService = inject(EntriesService);
  formService = inject(FormService);
  private userStore = inject(UserStore);
  allFields = computed(() => {
    return this.field.fieldGroup || [];
  });

  subFormOptions = {};
  subForm = new FormGroup({});

  enrollmentModule = computed(() => {
    return this.modulesStore.modules().find(module => module.code === 'enrollment');
  });

  myOrdersModule = computed(() => {
    return this.modulesStore.modules().find(module => module.code === 'my-orders');
  });

  enrollmentModuleFields = computed(() => {
    const generatedForm = this.modulesStore.generatedForms()[this.enrollmentModule().id];

    return generatedForm || [];
  });

  canReadAssignmentField(keyPath: string): boolean {
    const moduleId = this.myOrdersModule()?.id;
    return canReadFieldRight(
      effectiveFieldRightForKeyPath(
        moduleId ? this.modulesStore.generatedForms()[moduleId] : this.allFields(),
        keyPath,
        this.userStore.currentUserRoles()
      )
    );
  }

  readableReferenceLabel(reference: Entry | undefined, moduleCode: string): string {
    if (!reference) return '';
    const module = reference.moduleId
      ? this.modulesStore.getModuleById(reference.moduleId)
      : this.modulesStore.getModuleByCode(moduleCode);
    const titleKeyPath = module?.titelKeyPath;
    if (
      !module ||
      !canReadGeneratedField(
        this.modulesStore.generatedForms()[module.id],
        titleKeyPath,
        this.userStore.currentUserRoles()
      )
    )
      return '';
    return String(reference.selectedInput ?? UtilityService.keyPathToValue(reference, titleKeyPath) ?? '');
  }

  entry = signal<Entry>(undefined);
  isEditting = computed(() => {
    return this.entry()?.id !== undefined;
  });
  saved = signal(false);

  // Computed signal to check if assignment is closed
  isAssignmentClosed = computed(() => {
    if (!this.canReadAssignmentField('entry.status')) return false;
    const status = this.model?.status?.selectedInput?.toLowerCase() || '';
    return status.includes('afgerond');
  });

  private destroy$ = injectDestroy();

  constructor() {
    super();

    // Effect to enable/disable form based on assignment status
    effect(() => {
      const isClosed = this.isAssignmentClosed();
      if (isClosed) {
        this.subForm.disable();
      } else {
        this.subForm.enable();
      }
    });
  }

  ngOnInit() {
    this.fetchRelatedEnrollmentEntry();

    // Also subscribe to form value changes to monitor status changes
    this.field.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // The computed signal will automatically update and trigger the effect
    });
  }

  fetchRelatedEnrollmentEntry() {
    // get last entry from enrollment module linked to this entry
    this.entriesService
      .getEntries({
        moduleId: this.enrollmentModule().id,
        offset: 0,
        limit: 1,
        filter: `entry['my-order-id'] eq '${this.model?.['meta-data']?.['my-order-id']}'`,
        applyQueryParams: false,
      })
      .pipe(
        switchMap(entries => {
          if (entries.length > 0) {
            return of(entries[0]);
          } else {
            // If no entries found, fetch the entry from the my-orders module and fill my-order-id into the model
            return this.entriesService
              .getEntry(this.myOrdersModule().id, this.model?.['meta-data']?.['my-order-id'])
              .pipe(
                map(entry => {
                  return {
                    id: undefined,
                    entry: {
                      ...this.formService.getDefaultValues(this.enrollmentModuleFields()),
                      'public-assignment': this.options.formState.entry,
                      'my-order-id': entry.id,
                      company: entry.entry.company,
                    },
                  };
                })
              );
          }
        }),
        tap(entry => {
          this.entry.set(entry);
          this.subForm.patchValue(entry.entry);
        })
      )
      .subscribe();
  }

  ngAfterViewInit() {
    // Subscribe to form value changes if needed
    this.field.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      // Handle form value changes if needed
    });
  }

  getStatusClass(): string {
    if (!this.canReadAssignmentField('entry.status')) return 'status-default';
    const status = this.model?.status;
    const statusValue = status?.selectedInput?.toLowerCase() || '';

    if (statusValue.includes('open')) {
      return 'status-open';
    } else if (statusValue.includes('afgerond')) {
      return 'status-closed';
    } else if (statusValue.includes('geannulleerd')) {
      return 'status-cancelled';
    }

    return 'status-default';
  }

  formatDate(date: any): string {
    if (!date) return '';

    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (e) {
      return date.toString();
    }
  }

  formatRate(): string {
    const rate = this.model.rate;
    if (!rate) return '';

    const min = rate.min;
    const max = rate.max;

    if (min && max) {
      return `€${min} - €${max} per uur`;
    } else if (min) {
      return `€${min} per uur`;
    } else if (max) {
      return `Tot €${max} per uur`;
    }

    return '';
  }

  formatHours(): string {
    const hours = this.model?.hours;
    if (!hours) return '';

    return `${hours} uur`;
  }

  save() {
    // Extra check to prevent saving when assignment is closed
    if (this.isAssignmentClosed()) {
      this.utilityService.showMessage('Deze opdracht is afgerond. Inschrijven is niet meer mogelijk.');
      return;
    }

    this.subForm.markAllAsTouched();
    if (this.subForm.invalid || this.subForm.disabled) {
      const invalidControls = UtilityService.getAllInvalidControlsWithFormly(
        this.subForm,
        this.enrollmentModuleFields()
      );
      const errorMessages = invalidControls
        .filter(field => field.label)
        .map(field => `${field.label}: ${field.errors.join(', ')}`)
        .join('<br>');

      this.utilityService.showMessage(`Opslaan mislukt. Controleer alle velden nogmaals. ${errorMessages}`);
      return;
    }

    if (this.entry()?.id) {
      this.editSave();
    } else {
      this.addSave();
    }
  }

  addSave() {
    this.entriesService
      .addEntry(this.enrollmentModule().id, {
        archived: false,
        ...this.subForm.getRawValue(),
      } as Entry['entry'])
      .pipe(
        tap(() => {
          this.saved.set(true);
          this.utilityService.showMessage(`Inschrijving succesvol opgeslagen.`);
        })
      )
      .subscribe();
  }
  editSave() {
    this.entriesService
      .editEntry(this.enrollmentModule().id, this.entry().id, {
        ...this.entry(),
        entry: { ...this.entry().entry, ...this.subForm.getRawValue() },
      })
      .pipe(
        tap(() => {
          this.utilityService.showMessage(`Inschrijving succesvol opnieuw opgeslagen.`);
        })
      )
      .subscribe();
  }

  delete() {
    // Extra check to prevent deletion when assignment is closed
    if (this.isAssignmentClosed()) {
      this.utilityService.showMessage('Deze opdracht is afgerond. Je kunt je inschrijving niet meer intrekken.');
      return;
    }

    this.entriesService
      .delete(this.enrollmentModule().id, this.entry().id)
      .pipe(
        tap(() => {
          this.utilityService.showMessage(`Inschrijving ingetrokken.`);
          this.fetchRelatedEnrollmentEntry();
        })
      )
      .subscribe();
  }

  protected readonly EntryState = EntryState;
}
